import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@client/components/ui/button';
import { OAuthConnection } from '@client/components/OauthConnection';
import { OAuthRedirectPage } from '@client/components/OAuthCallback';
import { AuthInputTextField, AuthSeparator, TextButton } from '@client/components';
import { AdvisorOnboardingVideoSlide, AdvisorOnboardingActionCard } from '.';
import {
  CircleAlert,
  ClipboardList,
  ExternalLink,
  PartyPopper,
  PlayCircle,
  UserPlus,
  UserCog,
} from 'lucide-react';

import { useToast } from '@client/components/hooks/use-toast';

import {
  selectSessionAdvisor,
  postAdvisorUpdatesByAdvisorIdRequest,
  selectAdvisorState,
  resetAdvisorSettings,
} from '@client/model';
import { addHttpsIfNoScheme, isValidUrl } from '@shared';

export type OnboardingStepId =
  | 'welcome'
  | 'invite-client'
  | 'client-intake-form'
  | 'complete-profile';

interface CompletionStepProps {
  advisorId: string;
  completedSteps: string[];
}

const onboardingStepDetails: Record<
  OnboardingStepId,
  { icon: typeof PlayCircle; title: string; description: string; path: string }
> = {
  'welcome': {
    icon: PlayCircle,
    title: 'Watch Introduction Video',
    description: 'Learn how to use Waterlily effectively with your clients.',
    path: 'welcome',
  },
  'invite-client': {
    icon: UserPlus,
    title: 'Learn How to Invite Clients',
    description: 'Understand how to invite and manage clients on the platform.',
    path: 'invite-client',
  },
  'client-intake-form': {
    icon: ClipboardList,
    title: 'Review Client Intake Form',
    description: 'See how the client intake form works and what information is collected.',
    path: 'client-intake-form',
  },
  'complete-profile': {
    icon: UserCog,
    title: 'Complete Your Profile',
    description: 'Connect your email, add your business details and calendar link.',
    path: 'complete-profile',
  },
};

export function AdvisorOnboardingCompletionStep({
  advisorId,
  completedSteps,
}: CompletionStepProps) {
  const allSteps: OnboardingStepId[] = [
    'welcome',
    'invite-client',
    'client-intake-form',
    'complete-profile',
  ];

  const isAllStepsCompleted = allSteps.every(step => completedSteps.includes(step));

  return (
    <div className="flex flex-col items-center">
      {isAllStepsCompleted && <PartyPopper className="text-primary mx-auto size-24" />}
      <h1 className="mb-4 mt-10 text-center text-3xl font-bold text-gray-950 md:text-4xl">
        {isAllStepsCompleted ? "You're All Set!" : 'Almost There!'}
      </h1>
      <p className="max-w-3xl text-balance text-center text-base text-gray-600 md:text-lg">
        {isAllStepsCompleted
          ? 'Your Waterlily account is ready, continue to your dashboard and start inviting clients and provide personalized long-term care predictions.'
          : 'You skipped some steps during onboarding. You can go back to complete them or continue to your dashboard.'}
      </p>
      <div className="mt-8 w-full max-w-2xl space-y-4">
        {allSteps.map(stepId => {
          const step = onboardingStepDetails[stepId];
          return (
            <AdvisorOnboardingActionCard
              key={stepId}
              icon={step.icon}
              title={step.title}
              description={step.description}
              path={`/advisors/${advisorId}/onboarding/${step.path}`}
              isCompleted={completedSteps.includes(stepId)}
            />
          );
        })}
      </div>
    </div>
  );
}

export function AdvisorOnboardingCompleteYourProfileStep({ refetch }: { refetch: () => void }) {
  const { toast } = useToast();
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);
  const { advisorSettings } = useSelector(selectAdvisorState);

  const [schedulingLinkUrl, setSchedulingLinkUrl] = useState(advisor.schedulingLinkUrl ?? '');

  const [address, setAddress] = useState<Partial<Address>>({
    streetAddress: advisor.advisorBusinessAddress?.streetAddress ?? '',
    apartment: advisor.advisorBusinessAddress?.apartment ?? '',
    city: advisor.advisorBusinessAddress?.city ?? '',
    state: advisor.advisorBusinessAddress?.state ?? '',
    zipCode: advisor.advisorBusinessAddress?.zipCode ?? '',
    country: advisor.advisorBusinessAddress?.country ?? 'US',
  });

  const [errors, setErrors] = useState<{
    schedulingLinkUrl?: boolean;
    state?: boolean;
    zipCode?: boolean;
  }>({});

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Refetch checklist and show toast when OAuth connection changes
  const googleEmail = advisor.advisorOauthSettings?.google?.userEmail;
  const microsoftEmail = advisor.advisorOauthSettings?.microsoft?.userEmail;

  const prevGoogleEmail = useRef(googleEmail);
  const prevMicrosoftEmail = useRef(microsoftEmail);

  useEffect(() => {
    const googleConnected = googleEmail && !prevGoogleEmail.current;
    const microsoftConnected = microsoftEmail && !prevMicrosoftEmail.current;

    if (googleConnected || microsoftConnected) {
      const provider = googleConnected ? 'Google' : 'Microsoft';
      toast({
        title: 'Email Account Connected',
        description: `Your ${provider} account has been connected successfully.`,
        variant: 'default',
        className: '-translate-y-2 border-green-500 bg-green-50 text-green-800',
        duration: 5000,
      });
      refetch();
    }

    prevGoogleEmail.current = googleEmail;
    prevMicrosoftEmail.current = microsoftEmail;
  }, [googleEmail, microsoftEmail, refetch, toast]);

  // Handle successful save
  useEffect(() => {
    if (advisorSettings.status === 'complete' && !advisorSettings.isLoading) {
      toast({
        title: 'Profile Updated',
        description: 'Your calendar link and business address have been saved.',
        variant: 'default',
        className: '-translate-y-2 border-green-500 bg-green-50 text-green-800',
        duration: 5000,
      });
      dispatch(resetAdvisorSettings());
      setHasUnsavedChanges(false);
      refetch();
    }
  }, [advisorSettings.status, advisorSettings.isLoading, dispatch, refetch, toast]);

  // Sync form state when advisor data changes (e.g., after OAuth redirect back)
  useEffect(() => {
    setSchedulingLinkUrl(advisor.schedulingLinkUrl ?? '');
    setAddress({
      streetAddress: advisor.advisorBusinessAddress?.streetAddress ?? '',
      apartment: advisor.advisorBusinessAddress?.apartment ?? '',
      city: advisor.advisorBusinessAddress?.city ?? '',
      state: advisor.advisorBusinessAddress?.state ?? '',
      zipCode: advisor.advisorBusinessAddress?.zipCode ?? '',
      country: advisor.advisorBusinessAddress?.country ?? 'US',
    });
  }, [advisor.advisorId, advisor.schedulingLinkUrl, advisor.advisorBusinessAddress]);

  function handleAddressChange(field: keyof Address, value: string) {
    setAddress(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  }

  function handleSchedulingLinkChange(value: string) {
    setSchedulingLinkUrl(value);
    setHasUnsavedChanges(true);
  }

  function validateForm(): boolean {
    const newErrors: typeof errors = {};

    if (schedulingLinkUrl) {
      const formattedUrl = addHttpsIfNoScheme(schedulingLinkUrl.trim());
      if (!isValidUrl(formattedUrl)) {
        newErrors.schedulingLinkUrl = true;
      }
    }

    if (address.state && !/^[A-Z]{2}$/.test(address.state)) {
      newErrors.state = true;
    }

    if (address.zipCode && !/^\d{5}(-\d{4})?$/.test(address.zipCode)) {
      newErrors.zipCode = true;
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }

  function handleSave() {
    if (!validateForm()) return;

    const formattedUrl = schedulingLinkUrl ? addHttpsIfNoScheme(schedulingLinkUrl.trim()) : '';

    const hasAddressData = Object.values(address).some(
      value => value && typeof value === 'string' && value.trim() !== '',
    );

    const advisorBusinessAddress: AdvisorAddress | undefined = hasAddressData
      ? {
          advisorId: advisor.advisorId,
          addressType: 'business' as const,
          streetAddress: address.streetAddress ?? '',
          apartment: address.apartment ?? '',
          city: address.city ?? '',
          state: address.state ?? '',
          zipCode: address.zipCode ?? '',
          country: address.country ?? 'US',
        }
      : undefined;

    dispatch(
      postAdvisorUpdatesByAdvisorIdRequest({
        ...advisor,
        schedulingLinkUrl: formattedUrl,
        advisorBusinessAddress,
      }),
    );
  }

  function handleCancel() {
    setSchedulingLinkUrl(advisor.schedulingLinkUrl ?? '');
    setAddress({
      streetAddress: advisor.advisorBusinessAddress?.streetAddress ?? '',
      apartment: advisor.advisorBusinessAddress?.apartment ?? '',
      city: advisor.advisorBusinessAddress?.city ?? '',
      state: advisor.advisorBusinessAddress?.state ?? '',
      zipCode: advisor.advisorBusinessAddress?.zipCode ?? '',
      country: advisor.advisorBusinessAddress?.country ?? 'US',
    });
    setErrors({});
    setHasUnsavedChanges(false);
  }

  return (
    <div className="flex flex-col items-center">
      <h1 className="mb-4 text-center text-3xl font-bold text-gray-950 md:text-4xl">
        Complete Your Profile
      </h1>
      <p className="max-w-3xl text-balance text-center text-base text-gray-600 md:text-lg">
        Set up your advisor profile by connecting your email, adding your calendar link, and
        business address.
      </p>

      <div className="mt-8 w-full max-w-2xl">
        <div className="rounded-xl bg-white px-6 py-8 shadow-sm ring-1 ring-gray-900/5">
          <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-8">
            <AuthSeparator
              className="col-span-full sm:col-span-8"
              text="Connect Your Email Account"
            />
            <div className="col-span-full sm:col-span-8">
              <p className="mb-4 text-sm text-gray-600">
                Send personalized emails directly from your email account to strengthen client
                relationships.
              </p>
              <div className="flex flex-col gap-4">
                <OAuthConnection
                  provider="google"
                  redirectPage={OAuthRedirectPage.ADVISOR_ONBOARDING}
                />
                <OAuthConnection
                  provider="microsoft"
                  redirectPage={OAuthRedirectPage.ADVISOR_ONBOARDING}
                />
              </div>
            </div>

            <AuthSeparator
              className="col-span-full sm:col-span-8"
              text="Calendar Booking Settings"
            />
            <AuthInputTextField
              id="schedulingLinkUrl"
              label="Scheduling Link (allow clients to book time on your calendar)"
              value={schedulingLinkUrl}
              onChange={e => handleSchedulingLinkChange(e.target.value)}
              required={false}
              error={errors.schedulingLinkUrl}
              className="col-span-full mt-0 sm:col-span-8"
            />
            {!errors.schedulingLinkUrl && schedulingLinkUrl && (
              <TextButton
                buttonLabel="Open link to test"
                href={addHttpsIfNoScheme(schedulingLinkUrl.trim())}
                className="-mt-5 ml-1 text-sm sm:col-span-8"
              />
            )}

            <AuthSeparator
              className="col-span-full sm:col-span-8"
              text="Business Address"
            />
            <AuthInputTextField
              id="business-street-address"
              label="Street Address"
              value={address.streetAddress ?? ''}
              onChange={e => handleAddressChange('streetAddress', e.target.value)}
              className="col-span-full mt-0 sm:col-span-6"
              required={false}
              placeholder="123 Main Street"
            />
            <AuthInputTextField
              id="business-apartment"
              label="Apt/Suite"
              value={address.apartment ?? ''}
              onChange={e => handleAddressChange('apartment', e.target.value)}
              className="col-span-full mt-0 sm:col-span-2"
              required={false}
              placeholder="Suite 200"
            />
            <AuthInputTextField
              id="business-city"
              label="City"
              value={address.city ?? ''}
              onChange={e => handleAddressChange('city', e.target.value)}
              className="col-span-full mt-0 sm:col-span-2"
              required={false}
              placeholder="San Francisco"
            />
            <AuthInputTextField
              id="business-state"
              label="State"
              value={address.state ?? ''}
              onChange={e => handleAddressChange('state', e.target.value.toUpperCase())}
              className="mt-0 sm:col-span-2"
              required={false}
              error={errors.state}
              placeholder="CA"
            />
            <AuthInputTextField
              id="business-zip-code"
              label="ZIP Code"
              value={address.zipCode ?? ''}
              onChange={e => {
                const value = e.target.value.replace(/[^\d-]/g, '');
                handleAddressChange('zipCode', value);
              }}
              className="mt-0 sm:col-span-2"
              required={false}
              error={errors.zipCode}
              placeholder="94105"
            />
            <AuthInputTextField
              id="business-country"
              label="Country"
              value={address.country ?? ''}
              onChange={e => handleAddressChange('country', e.target.value)}
              className="mt-0 sm:col-span-2"
              required={false}
              placeholder="US"
            />

            <div className="col-span-full flex items-center justify-end gap-x-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={!hasUnsavedChanges}
                className="px-6 py-3"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={!hasUnsavedChanges || advisorSettings.isLoading}
                className="px-6 py-3"
              >
                {advisorSettings.isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface VideoStepProps {
  onVideoPlayed?: () => void;
}

export function AdvisorOnboardingClientIntakeFormStep({ onVideoPlayed }: VideoStepProps) {
  return (
    <AdvisorOnboardingVideoSlide
      title="Client Intake Form"
      description="Clients complete a comprehensive intake form with their health history, lifestyle factors,
      and family information."
      videoId="eb9018c4621c4e8eaff3a10b53480c00"
      onVideoPlayed={onVideoPlayed}
    />
  );
}

export function AdvisorOnboardingInvitingClientsStep({ onVideoPlayed }: VideoStepProps) {
  return (
    <AdvisorOnboardingVideoSlide
      title="Inviting Clients"
      description="From your dashboard, you can invite clients via email. They'll receive a personalized link
      to complete their intake form."
      videoId="90255565dc404610846adecbd7d82049"
      onVideoPlayed={onVideoPlayed}
    />
  );
}

export function AdvisorOnboardingWelcomeStep({ onVideoPlayed }: VideoStepProps) {
  return (
    <AdvisorOnboardingVideoSlide
      title="Welcome to Waterlily"
      description="Learn how to use Waterlily to provide personalized long-term care predictions for your
  clients."
      videoId="129020a636a542969d3b8dcddf51d43f"
      onVideoPlayed={onVideoPlayed}
    />
  );
}
